import { useState, type FormEvent } from 'react';
import { CheckboxField } from '@/components/checkbox-field';
import { LEGAL_URLS } from '@/components/legal-links';
import { Alert } from '@/components/alert';
import { Button } from '@/components/button';
import { PasswordField } from '@/components/password-field';
import { TextField } from '@/components/text-field';
import type { Copy } from '@/lib/messages';
import { PASSWORD_MIN_LENGTH } from '@repo/auth-contracts';

export interface ProfileValues {
  givenName: string;
  familyName: string;
  birthdate: string;
  /** Omitted entirely when left blank — the account is simply passwordless. */
  password?: string;
  /**
   * Agreement to the documents in force. Both are required and no account is
   * created without them, which is why they live on THIS step rather than a
   * later one (docs/auth/40-features/terms-acceptance.md).
   */
  acceptTerms: true;
  acceptPrivacy: true;
}

interface CompleteProfileStepProps {
  copy: Copy;
  pending: boolean;
  error: string | null;
  minimumAge: number;
  /**
   * Names a social provider already gave us. A starting value, not a fixed
   * one: the fields stay editable, and the server validates whatever is
   * actually submitted (docs/auth/40-features/signup.md).
   */
  prefill?: { givenName?: string; familyName?: string } | null;
  onSubmit: (values: ProfileValues) => void;
}

/**
 * The one sign-up screen (ADR 0017), reached once an identity is proven —
 * today by an email code, from M10 also by a social provider. The age gate is
 * re-checked server-side; this only keeps the obvious cases out of a round
 * trip.
 */
export function CompleteProfileStep({
  copy,
  pending,
  error,
  minimumAge,
  prefill,
  onSubmit,
}: CompleteProfileStepProps) {
  const [givenName, setGivenName] = useState(prefill?.givenName ?? '');
  const [familyName, setFamilyName] = useState(prefill?.familyName ?? '');
  const [birthdate, setBirthdate] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);

  // A blank password is valid (it means "none"); a too-short one is not.
  const passwordUsable =
    password === '' || password.length >= PASSWORD_MIN_LENGTH;
  const complete =
    givenName.trim() !== '' &&
    familyName.trim() !== '' &&
    birthdate !== '' &&
    passwordUsable &&
    acceptTerms &&
    acceptPrivacy;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!complete) return;
    onSubmit({
      givenName: givenName.trim(),
      familyName: familyName.trim(),
      birthdate,
      ...(password === '' ? {} : { password }),
      acceptTerms: true,
      acceptPrivacy: true,
    });
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      {error ? <Alert tone="error">{error}</Alert> : null}
      <TextField
        id="profile-given-name"
        label={copy.signupGivenName}
        value={givenName}
        onChange={(event) => setGivenName(event.target.value)}
        autoComplete="given-name"
        maxLength={100}
        autoFocus={!prefill?.givenName}
        required
      />
      <TextField
        id="profile-family-name"
        label={copy.signupFamilyName}
        value={familyName}
        onChange={(event) => setFamilyName(event.target.value)}
        autoComplete="family-name"
        maxLength={100}
        autoFocus={Boolean(prefill?.givenName) && !prefill?.familyName}
        required
      />
      <TextField
        id="profile-birthdate"
        label={copy.birthdateLabel}
        type="date"
        value={birthdate}
        onChange={(event) => setBirthdate(event.target.value)}
        autoComplete="bday"
        max={new Date().toISOString().slice(0, 10)}
        hint={copy.birthdateHint(minimumAge)}
        required
      />

      {/* Sign-in here is passwordless by default (ADR 0007), so a password
          field on the critical path would ask every new user to invent one
          they will probably never use. Folded away, and one-way: once opened
          it stays open, so a half-typed password can never end up hidden and
          silently blocking the submit button. */}
      {showPassword ? (
        <PasswordField
          id="profile-password"
          label={copy.passwordOptionalLabel}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          showLabel={copy.passwordShow}
          hideLabel={copy.passwordHide}
          hint={copy.passwordHint}
          autoFocus
        />
      ) : (
        <Button
          type="button"
          variant="link"
          onClick={() => setShowPassword(true)}
          disabled={pending}
        >
          {copy.passwordAddOptional}
        </Button>
      )}

      <div className="space-y-2">
        <CheckboxField
          id="signup-accept-terms"
          checked={acceptTerms}
          onChange={(event) => setAcceptTerms(event.target.checked)}
          disabled={pending}
          label={
            <>
              {copy.termsCheckboxBefore}
              <a
                href={LEGAL_URLS.terms}
                target="_blank"
                rel="noreferrer noopener"
                className="focus-ring rounded underline hover:text-buds-dark"
              >
                {copy.legalTerms}
              </a>
              {copy.termsCheckboxAfter}
            </>
          }
        />
        <CheckboxField
          id="signup-accept-privacy"
          checked={acceptPrivacy}
          onChange={(event) => setAcceptPrivacy(event.target.checked)}
          disabled={pending}
          label={
            <>
              {copy.privacyCheckboxBefore}
              <a
                href={LEGAL_URLS.privacy}
                target="_blank"
                rel="noreferrer noopener"
                className="focus-ring rounded underline hover:text-buds-dark"
              >
                {copy.legalPrivacy}
              </a>
              {copy.privacyCheckboxAfter}
            </>
          }
        />
      </div>

      <Button type="submit" loading={pending} disabled={!complete}>
        {copy.profileSubmit}
      </Button>
    </form>
  );
}
